import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  RefreshCw,
  Users,
  ClipboardList,
  AlertCircle,
  CheckCircle,
  PhoneIncoming,
  Activity,
  UserCheck,
  Briefcase,
  Phone,
  Mic,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
  PieChart,
  Pie,
} from 'recharts';
import { AdminService, supabase } from '../../services/adminService';

const toYMD = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const fmtNum = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString();
};

const fmtDateTime = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
};

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition">
    <div className="flex items-center justify-between mb-3">
      <div
        className="w-11 h-11 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${color}1A` }}
      >
        <Icon size={22} color={color} />
      </div>
      {sub ? <span className="text-xs text-gray-500">{sub}</span> : null}
    </div>
    <h3 className="text-2xl font-bold text-gray-900 leading-tight">{value}</h3>
    <p className="text-sm text-gray-500 mt-1">{label}</p>
  </div>
);

const WFHDashboard = () => {
  const [sp, setSp] = useSearchParams();
  const [fromDate, setFromDate] = useState(() => sp.get('from') || toYMD(new Date()));
  const [toDate, setToDate] = useState(() => sp.get('to') || toYMD(new Date()));
  const [tlFilter, setTlFilter] = useState(() => sp.get('tl') || '');

  useEffect(() => {
    const params = new URLSearchParams();
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    if (tlFilter) params.set('tl', tlFilter);
    setSp(params, { replace: true });
  }, [fromDate, toDate, tlFilter]);

  const { data: teamLeads = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['adminTeamLeads'],
    queryFn: AdminService.getTeamLeads,
    staleTime: 5 * 60 * 1000,
  });

  const { data: telecallers = [] } = useQuery({
    queryKey: ['adminTelecallers'],
    queryFn: AdminService.getTelecallers,
    staleTime: 5 * 60 * 1000,
  });

  const usersById = useMemo(() => {
    const map = new Map();
    [...teamLeads, ...telecallers].forEach((u) => map.set(String(u.id), u));
    return map;
  }, [teamLeads, telecallers]);

  const targetTeamLeads = useMemo(() => {
    if (!tlFilter) return teamLeads;
    return teamLeads.filter((tl) => String(tl.id) === String(tlFilter));
  }, [teamLeads, tlFilter]);

  const tlIds = useMemo(() => targetTeamLeads.map((tl) => String(tl.id)).sort().join(','), [
    targetTeamLeads,
  ]);

  const {
    data: aggregated,
    isFetching: isFetchingStats,
    refetch,
  } = useQuery({
    queryKey: ['wfhDashboardStats', tlIds, fromDate, toDate],
    enabled: targetTeamLeads.length > 0,
    queryFn: async () => {
      const results = await Promise.all(
        targetTeamLeads.map(async (tl) => {
          const [statsRes, callsRes] = await Promise.all([
            supabase.rpc('admin_wfh_employee_stats', {
              p_team_lead_id: tl.id,
              p_batch_name: null,
              p_from_date: fromDate || null,
              p_to_date: toDate || null,
            }),
            supabase.rpc('admin_wfh_employee_call_stats', {
              p_team_lead_id: tl.id,
              p_from_date: fromDate || null,
              p_to_date: toDate || null,
            }),
          ]);
          if (statsRes.error) {
            console.warn('admin_wfh_employee_stats failed for TL', tl.id, statsRes.error);
          }
          if (callsRes.error) {
            console.warn('admin_wfh_employee_call_stats failed for TL', tl.id, callsRes.error);
          }
          const callMap = {};
          (Array.isArray(callsRes.data) ? callsRes.data : []).forEach((c) => {
            if (!c?.telecaller_id) return;
            callMap[String(c.telecaller_id)] = c;
          });
          return {
            tl,
            rows: Array.isArray(statsRes.data) ? statsRes.data : [],
            callMap,
          };
        })
      );
      return results;
    },
  });

  const flatEmployeeRows = useMemo(() => {
    const list = aggregated || [];
    const out = [];
    list.forEach(({ tl, rows, callMap }) => {
      rows.forEach((r) => {
        const u = usersById.get(String(r.telecaller_id));
        const cs = callMap?.[String(r.telecaller_id)] || {};
        out.push({
          ...r,
          assigned_count: Number(r.assigned_count) || 0,
          no_disposition_count: Number(r.no_disposition_count) || 0,
          disposed_count: Number(r.disposed_count) || 0,
          confirmed_count: Number(r.confirmed_count) || 0,
          callback_count: Number(r.callback_count) || 0,
          disposed_by_tc_count: Number(r.disposed_by_tc_count) || 0,
          _tlId: tl.id,
          _tlName: tl.name || tl.email || String(tl.id),
          _name: u?.name || u?.email || String(r.telecaller_id),
          _email: u?.email || '',
          _total_calls: Number(cs.total_calls) || 0,
          _answered_calls: Number(cs.answered_calls) || 0,
          _missed_calls: Number(cs.missed_calls) || 0,
          _sarv_calls: Number(cs.sarv_calls) || 0,
          _tata_calls: Number(cs.tata_calls) || 0,
        });
      });
    });
    return out;
  }, [aggregated, usersById]);

  const totals = useMemo(() => {
    const list = flatEmployeeRows;
    const sum = (k) => list.reduce((s, r) => s + (Number(r[k]) || 0), 0);
    return {
      assigned: sum('assigned_count'),
      noDisposition: sum('no_disposition_count'),
      disposed: sum('disposed_count'),
      confirmed: sum('confirmed_count'),
      callback: sum('callback_count'),
      disposedBy: sum('disposed_by_tc_count'),
      totalCalls: sum('_total_calls'),
      answeredCalls: sum('_answered_calls'),
      sarvCalls: sum('_sarv_calls'),
      tataCalls: sum('_tata_calls'),
      activeEmployees: list.filter(
        (r) => r.assigned_count > 0 || r.disposed_by_tc_count > 0 || r._total_calls > 0
      ).length,
      totalEmployees: list.length,
      totalTeamLeads: targetTeamLeads.length,
    };
  }, [flatEmployeeRows, targetTeamLeads]);

  const tlBreakdown = useMemo(() => {
    const map = new Map();
    flatEmployeeRows.forEach((r) => {
      const key = String(r._tlId);
      if (!map.has(key)) {
        map.set(key, {
          tlId: r._tlId,
          tlName: r._tlName,
          assigned: 0,
          noDisposition: 0,
          disposed: 0,
          confirmed: 0,
          callback: 0,
          disposedBy: 0,
          totalCalls: 0,
          answeredCalls: 0,
          sarvCalls: 0,
          tataCalls: 0,
          employees: 0,
        });
      }
      const t = map.get(key);
      t.assigned += r.assigned_count;
      t.noDisposition += r.no_disposition_count;
      t.disposed += r.disposed_count;
      t.confirmed += r.confirmed_count;
      t.callback += r.callback_count;
      t.disposedBy += r.disposed_by_tc_count;
      t.totalCalls += r._total_calls;
      t.answeredCalls += r._answered_calls;
      t.sarvCalls += r._sarv_calls;
      t.tataCalls += r._tata_calls;
      t.employees += 1;
    });
    return Array.from(map.values()).sort((a, b) => b.disposed - a.disposed);
  }, [flatEmployeeRows]);

  const topEmployees = useMemo(() => {
    return [...flatEmployeeRows]
      .sort((a, b) => b.disposed_count - a.disposed_count || b.assigned_count - a.assigned_count)
      .slice(0, 15)
      .map((r) => ({
        name: r._name?.length > 14 ? `${r._name.slice(0, 13)}…` : r._name,
        Disposed: r.disposed_count,
        Assigned: r.assigned_count,
        Confirmed: r.confirmed_count,
      }));
  }, [flatEmployeeRows]);

  const dispositionPie = useMemo(() => {
    const data = [
      { name: 'Confirmed', value: totals.confirmed, color: '#10B981' },
      { name: 'Callback', value: totals.callback, color: '#3B82F6' },
      {
        name: 'Other Disposed',
        value: Math.max(0, totals.disposed - totals.confirmed - totals.callback),
        color: '#8B5CF6',
      },
      { name: 'No Disposition', value: totals.noDisposition, color: '#F59E0B' },
    ].filter((d) => d.value > 0);
    return data;
  }, [totals]);

  const sortedEmployeeRows = useMemo(() => {
    return [...flatEmployeeRows].sort((a, b) => {
      if (b.assigned_count !== a.assigned_count) return b.assigned_count - a.assigned_count;
      return b.disposed_count - a.disposed_count;
    });
  }, [flatEmployeeRows]);

  const buildBackToReportUrl = () => {
    const params = new URLSearchParams();
    if (tlFilter) params.set('tl', tlFilter);
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    return `/wfh-employee-report?${params.toString()}`;
  };

  const buildTcDetailUrl = (r) => {
    const params = new URLSearchParams();
    if (r._tlId) params.set('teamLeadId', r._tlId);
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    if (r._name) params.set('name', r._name);
    if (r._email) params.set('email', r._email);
    return `/wfh-employee-report/telecaller/${encodeURIComponent(
      String(r.telecaller_id)
    )}?${params.toString()}`;
  };

  const isBusy = isLoadingUsers || isFetchingStats;
  const dateLabel =
    fromDate && toDate
      ? fromDate === toDate
        ? new Date(`${fromDate}T00:00:00`).toLocaleDateString()
        : `${new Date(`${fromDate}T00:00:00`).toLocaleDateString()} → ${new Date(
            `${toDate}T00:00:00`
          ).toLocaleDateString()}`
      : '—';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link
            to={buildBackToReportUrl()}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeft size={16} className="mr-1" />
            Back to Employee Report
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">WFH Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Overall WFH performance overview for{' '}
            <span className="font-medium text-gray-800">{dateLabel}</span>
            {tlFilter
              ? ` · TL: ${
                  teamLeads.find((tl) => String(tl.id) === String(tlFilter))?.name ||
                  'Selected TL'
                }`
              : ' · All Team Leads'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
            disabled={isBusy}
          >
            <RefreshCw size={16} className={isFetchingStats ? 'animate-spin' : ''} />
            <span>{isFetchingStats ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              max={toDate || undefined}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={fromDate || undefined}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Team Lead</label>
            <select
              value={tlFilter}
              onChange={(e) => setTlFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              disabled={isLoadingUsers}
            >
              <option value="">All Team Leads</option>
              {teamLeads.map((tl) => (
                <option key={tl.id} value={tl.id}>
                  {tl.name || tl.email || tl.id}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={() => {
                const t = toYMD(new Date());
                setFromDate(t);
                setToDate(t);
              }}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => {
                const end = new Date();
                const start = new Date();
                start.setDate(end.getDate() - 6);
                setFromDate(toYMD(start));
                setToDate(toYMD(end));
              }}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
            >
              Last 7 days
            </button>
            <button
              type="button"
              onClick={() => {
                const end = new Date();
                const start = new Date();
                start.setDate(end.getDate() - 29);
                setFromDate(toYMD(start));
                setToDate(toYMD(end));
              }}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
            >
              30 days
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={ClipboardList}
          label="Total Assigned"
          value={fmtNum(totals.assigned)}
          color="#6366F1"
          sub={`${totals.totalEmployees} TCs`}
        />
        <StatCard
          icon={AlertCircle}
          label="No Disposition"
          value={fmtNum(totals.noDisposition)}
          color="#F59E0B"
          sub={
            totals.assigned
              ? `${Math.round((totals.noDisposition / totals.assigned) * 100)}%`
              : null
          }
        />
        <StatCard
          icon={CheckCircle}
          label="Total Disposed"
          value={fmtNum(totals.disposed)}
          color="#10B981"
          sub={
            totals.assigned
              ? `${Math.round((totals.disposed / totals.assigned) * 100)}%`
              : null
          }
        />
        <StatCard
          icon={UserCheck}
          label="Confirmed"
          value={fmtNum(totals.confirmed)}
          color="#059669"
          sub={
            totals.disposed
              ? `${Math.round((totals.confirmed / totals.disposed) * 100)}% of disposed`
              : null
          }
        />
        <StatCard
          icon={PhoneIncoming}
          label="Callbacks"
          value={fmtNum(totals.callback)}
          color="#3B82F6"
        />
        <StatCard
          icon={Activity}
          label="Disposed By (TC actions)"
          value={fmtNum(totals.disposedBy)}
          color="#8B5CF6"
        />
        <StatCard
          icon={Users}
          label="Active Employees"
          value={fmtNum(totals.activeEmployees)}
          color="#EC4899"
          sub={`of ${totals.totalEmployees}`}
        />
        <StatCard
          icon={Briefcase}
          label="Team Leads"
          value={fmtNum(totals.totalTeamLeads)}
          color="#0EA5E9"
        />
      </div>

      {/* Calls overview (Sarv + Tata) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Phone}
          label="Total Calls"
          value={fmtNum(totals.totalCalls)}
          color="#0F766E"
          sub="Sarv + Tata"
        />
        <StatCard
          icon={Mic}
          label="Answered Calls"
          value={fmtNum(totals.answeredCalls)}
          color="#16A34A"
          sub={
            totals.totalCalls
              ? `${Math.round((totals.answeredCalls / totals.totalCalls) * 100)}% answer rate`
              : null
          }
        />
        <StatCard
          icon={Phone}
          label="Sarv Calls"
          value={fmtNum(totals.sarvCalls)}
          color="#7C3AED"
          sub={
            totals.totalCalls
              ? `${Math.round((totals.sarvCalls / totals.totalCalls) * 100)}% of calls`
              : null
          }
        />
        <StatCard
          icon={Phone}
          label="Tata Calls"
          value={fmtNum(totals.tataCalls)}
          color="#EA580C"
          sub={
            totals.totalCalls
              ? `${Math.round((totals.tataCalls / totals.totalCalls) * 100)}% of calls`
              : null
          }
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Top 15 Employees by Disposed</h2>
            <span className="text-xs text-gray-500">{dateLabel}</span>
          </div>
          {topEmployees.length === 0 ? (
            <div className="text-gray-500 text-sm py-12 text-center">
              {isBusy ? 'Loading data...' : 'No data for the selected range.'}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={topEmployees} margin={{ top: 8, right: 16, left: 0, bottom: 32 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis
                  dataKey="name"
                  angle={-25}
                  textAnchor="end"
                  height={60}
                  tick={{ fontSize: 11, fill: '#475569' }}
                />
                <YAxis tick={{ fontSize: 11, fill: '#475569' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Assigned" fill="#6366F1" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Disposed" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Confirmed" fill="#059669" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Disposition Mix</h2>
          {dispositionPie.length === 0 ? (
            <div className="text-gray-500 text-sm py-12 text-center">No data.</div>
          ) : (
            <ResponsiveContainer width="100%" height={340}>
              <PieChart>
                <Pie
                  data={dispositionPie}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={100}
                  paddingAngle={2}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                >
                  {dispositionPie.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {!tlFilter && tlBreakdown.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Team Lead-wise Breakdown</h2>
            <span className="text-sm text-gray-500">{tlBreakdown.length} TLs</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team Lead
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employees
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    No Disposition
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Disposed
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confirmed
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Callback
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Calls
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Answered
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sarv
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tata
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conv %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {tlBreakdown.map((t) => {
                  const pct = t.assigned ? Math.round((t.disposed / t.assigned) * 100) : 0;
                  const ansPct = t.totalCalls ? Math.round((t.answeredCalls / t.totalCalls) * 100) : 0;
                  const reportUrl = `/wfh-employee-report?tl=${encodeURIComponent(
                    t.tlId
                  )}&from=${fromDate}&to=${toDate}`;
                  return (
                    <tr key={String(t.tlId)} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <Link
                          to={reportUrl}
                          className="font-medium text-primary-700 hover:text-primary-800 hover:underline"
                          title="Open Team Lead's Employee Report"
                        >
                          {t.tlName}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">{fmtNum(t.employees)}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {fmtNum(t.assigned)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-amber-700">
                        {fmtNum(t.noDisposition)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-700">
                        {fmtNum(t.disposed)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-800">
                        {fmtNum(t.confirmed)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-blue-700">
                        {fmtNum(t.callback)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {fmtNum(t.totalCalls)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-700">
                        {fmtNum(t.answeredCalls)}
                        {t.totalCalls > 0 && (
                          <span className="block text-[10px] text-gray-400 font-normal">{ansPct}%</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-purple-700">
                        {fmtNum(t.sarvCalls)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-orange-700">
                        {fmtNum(t.tataCalls)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">{pct}%</td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.totalEmployees)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.assigned)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.noDisposition)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.disposed)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.confirmed)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.callback)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.totalCalls)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.answeredCalls)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.sarvCalls)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.tataCalls)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {totals.assigned
                      ? `${Math.round((totals.disposed / totals.assigned) * 100)}%`
                      : '0%'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">All Employees · Detailed</h2>
          </div>
          <div className="text-sm text-gray-600">
            {sortedEmployeeRows.length} employees · {dateLabel}
          </div>
        </div>

        {isBusy && sortedEmployeeRows.length === 0 ? (
          <div className="text-gray-600">Loading...</div>
        ) : sortedEmployeeRows.length === 0 ? (
          <div className="text-gray-600">No data found for the selected range.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Telecaller
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team Lead
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    No Disp.
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Disposed
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confirmed
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Callback
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Disp. By
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Calls
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Answered
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sarv
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tata
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Claimed
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Disposition
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {sortedEmployeeRows.map((r) => {
                  const ansPct = r._total_calls
                    ? Math.round((r._answered_calls / r._total_calls) * 100)
                    : 0;
                  return (
                    <tr
                      key={`${r._tlId}-${r.telecaller_id}`}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={buildTcDetailUrl(r)}
                          className="font-medium text-primary-700 hover:text-primary-800 hover:underline"
                          title="Open detailed report"
                        >
                          {r._name}
                        </Link>
                        <div className="text-xs text-gray-500">{r._email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{r._tlName}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {fmtNum(r.assigned_count)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-amber-700">
                        {fmtNum(r.no_disposition_count)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {fmtNum(r.disposed_count)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-700">
                        {fmtNum(r.confirmed_count)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-blue-700">
                        {fmtNum(r.callback_count)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700">
                        {fmtNum(r.disposed_by_tc_count)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {fmtNum(r._total_calls)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-700">
                        {fmtNum(r._answered_calls)}
                        {r._total_calls > 0 && (
                          <span className="block text-[10px] text-gray-400 font-normal">{ansPct}%</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-purple-700 font-medium">
                        {fmtNum(r._sarv_calls)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-orange-700 font-medium">
                        {fmtNum(r._tata_calls)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {fmtDateTime(r.last_claimed_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {fmtDateTime(r.last_disposition_at)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                  <td className="px-4 py-3 text-sm text-gray-700">—</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.assigned)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.noDisposition)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.disposed)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.confirmed)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.callback)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.disposedBy)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.totalCalls)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.answeredCalls)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.sarvCalls)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {fmtNum(totals.tataCalls)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">—</td>
                  <td className="px-4 py-3 text-sm text-gray-700">—</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WFHDashboard;
